import React, { useEffect, useState, useRef } from "react";
import Container from "common/components/Container/Container";
import i18n from "internationalization/i18n";
import { useDispatch, useSelector } from "react-redux";
import { rootReducerType } from "../../redux/combineReducers";
import useWindowDimensions, { counterCommonItemsArrays, decodeJWT, getDataToDisplay } from "../../common/functions";
import { emailToUsername, getUpperCase } from "../../common/utils";
import {
    teamApi,
    useGetTeamQuery,
    useFefundTransactionMutation,
    useCreateTeamMutation,
    useUpdateTeamMutation,
    useDeteteTeamMutation
} from "../../services/teamService";
import { Button, DatePicker, Space, Modal, Select, Input, Form } from "antd";
import SelectFilter from "../../common/components/Select/Select";
import { useGetStationsQuery } from "../../services/stationsService";
import TeamTable
    from "./components/TeamTable";
import moment from "moment";
import { formatISO8601 } from "common/constants";
import { TransactionItem } from "services/utils/interfaces";
import Notification from "common/components/NotificationMessage/Notification";
import "./Team.less"
import type { SelectProps } from 'antd';

import {
    dashboardApi,
    useGetMenuQuery
} from "services/dashboardService";
import { first } from "lodash";

const { RangePicker } = DatePicker;

type Props = {
    shouldRefresh: boolean,
};

type FilterDates = {
    startTime?: string,
    endTime?: string,
};

export type Transactions = Array<TransactionItem>;

const Teams: React.FC<Props> = ({ shouldRefresh }): React.ReactElement => {

    const { isLoggedIn } = useSelector((state: rootReducerType) => state.authenticationReducer);

    const [params, setParams] = useState({
        userName: emailToUsername(decodeJWT(isLoggedIn).email),
    });
    const { data: menuData } = useGetMenuQuery(params);

    const [list, setList] = useState<any[]>([]);

    useEffect(() => {
        if (menuData) {
            const dashboardMenu = menuData[0].Dashboard
            const lists: any = []
            for (let i = 0; i < dashboardMenu.length; i++) {
                if (dashboardMenu[i] == "All") {
                } else {
                    lists.push({
                        value: dashboardMenu[i]['locationId.stationId'],
                        label: dashboardMenu[i]['stationName']
                    })
                }
            }
            setList(lists)
        }
    }, [menuData])

    const dispatch = useDispatch();

    const getFilterBy = (filter: string | null): string | undefined =>
        filter && [i18n.t("transactions.station"), i18n.t("transactions.type")].includes(filter)
            ? filter
            : undefined;

    const getFilterQueryString = (key: string | null, filterValue: string | null) =>
        key && filterValue
            ? key?.toLowerCase() + "_is_" + filterValue
            : undefined;

    const getKey = (filterBy?: string): string | null =>
        filterBy
            ? (filterBy === i18n.t("transactions.station") ? "stationId"
                : (filterBy === i18n.t("transactions.type")
                    ? "type"
                    : null))
            : null;

    const getDates = (startTime: string | null, endTime: string | null): FilterDates => {
        let startDate: string | undefined;
        let endDate: string | undefined;
        if (startTime && endTime) {
            startDate = moment(startTime).startOf("day").format('YYYY-MM-DDTHH:mm:ss');
            endDate = moment(endTime).endOf("day").format('YYYY-MM-DDTHH:mm:ss');
        }
        return { startTime: startDate, endTime: endDate };
    };

    const [limit] = useState(10);
    const [offset, setOffset] = useState<number>(0);
    const [previousOffset, setPreviousOffset] = useState<number>(offset);

    const [startTime, setStartTime] = useState<string | null>(null);
    const [endTime, setEndTime] = useState<string | null>(null);
    const [sort, setSort] = useState<string | null>(null);
    const [filter, setFilter] = useState<string | null>(null);
    const [filterValue, setFilterValue] = useState<string | null>(null);
    const [reset, setReset] = useState(false);
    const [dateRange, setDateRange] = useState<Array<moment.Moment> | undefined>(undefined);
    const [isOpenRangePicker, setIsOpenRangePicker] = useState<boolean>(false);

    const [transactions, setTransactions] = useState<Transactions>([]);

    const selectItems = [{ option: i18n.t("transactions.station") }, { option: i18n.t("transactions.type") }];
    const typeData = [i18n.t("transactions.level2"), i18n.t("transactions.dcfc")];
    const stations = useGetStationsQuery({
        userName: emailToUsername(decodeJWT(isLoggedIn).email)
    }).data;
    const stationData = stations ? Object.keys(stations) : [];

    const { data, refetch } = useGetTeamQuery({
        userName: emailToUsername(decodeJWT(isLoggedIn).email),
        limit: String(limit),
        offset: String(offset < 0 ? 0 : offset),
        sortBy: sort ? sort : undefined,
        filterBy: getFilterQueryString(getKey(getFilterBy(filter)), filterValue),
        ...getDates(startTime, endTime)
    });

    const [feFundTransaction, { isSuccess }] = useFefundTransactionMutation();

    const [createTeam, { isSuccess: isSuccessCreateTeam, data: createData }] = useCreateTeamMutation();

    const [updateTeam, { isSuccess: isSuccessUpdateTeam, data: updateData }] = useUpdateTeamMutation();

    const [deleteTeam, { isSuccess: isSuccessDeleteTeam, data: deleteData }] = useDeteteTeamMutation();

    // const data = "111"

    const { width } = useWindowDimensions();
    const dateFormat = "MMM D, YYYY";
    const dateFormatParams = 'YYYY-MM-DDTHH:mm:ss';

    const setRange = (values) => {
        setDateRange(values);
    };

    useEffect(() => {
        if (offset < previousOffset) return;
        if (offset === previousOffset) {
            const tempOffset = offset < 0 ? 0 : offset;
            setOffset(tempOffset);
            setPreviousOffset(tempOffset);
            const filterBy = getFilterBy(filter);
            const key = getKey(filterBy);
            dispatch(
                teamApi.endpoints.getTeam.initiate(
                    {
                        userName: emailToUsername(decodeJWT(isLoggedIn).email),
                        limit: String(limit),
                        offset: String(tempOffset),
                        sortBy: sort ? sort : undefined,
                        filterBy: getFilterQueryString(key, filterValue),
                        ...getDates(startTime, endTime)
                    },
                    { subscribe: false, forceRefetch: true }
                )
            );
        }

        // setReset(false);
    }, [offset]);

    useEffect(() => {
        const tempOffset = offset <= 0 ? offset - 1 : 0;
        setOffset(tempOffset);
        setPreviousOffset(tempOffset);
    }, [startTime, endTime, filterValue, sort]);

    useEffect(() => {
        if (!data) return;

        const tempOffset = offset < 0 ? 0 : offset;
        setOffset(tempOffset);
        setPreviousOffset(tempOffset);

        const counterCommonItems = counterCommonItemsArrays(data, transactions);

        const newItems = JSON.parse(JSON.stringify(
            offset <= 0
                ? [...data]
                : (counterCommonItems === 0
                    ? [...transactions, ...data]
                    : [...transactions]
                )));

        setTransactions(newItems);
        setPreviousOffset(offset);
    }, [JSON.stringify(data), offset, reset]);

    useEffect(() => {
        if (!reset) {
            setReset(false);

        }
        refetch();
    }, [refetch, reset]);

    useEffect(() => {
        refetch();
    }, [shouldRefresh]);

    useEffect(() => {
        if (!isOpenRangePicker) {
            setStartTime(dateRange ? moment(dateRange?.[0])?.format(dateFormatParams) : null);
            setEndTime(dateRange ? moment(dateRange?.[1])?.format(dateFormatParams) : null);
        }
    }, [isOpenRangePicker]);

    const handleReset = () => {
        const tempOffset = offset <= 0 ? offset - 1 : 0;
        setReset(true);
        setFilter(null);
        setFilterValue(null);
        setSort(null);
        setEndTime(null);
        setStartTime(null);
        setDateRange(undefined);
        setOffset(tempOffset);
        setPreviousOffset(tempOffset);
    };

    const [isModalOpen, setIsModalOpen] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleOk = () => {
        setIsModalOpen(false);
        if (refundParams) {
            feFundTransaction(refundParams)
        }
    };

    const [refundParams, setRefundParams] = useState(null)

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (isSuccess) {
            Notification({ type: "success", message: "feFundTransaction Success", description: "" });
        }
    }, [isSuccess])

    useEffect(() => {
        if (isSuccessCreateTeam) {
            Notification({ 
                type: createData?.success ? "success" : "warning",
                message: createData?.success ? createData.message : createData.message, 
                description: "" 
            });
            refetch()
        }
    }, [isSuccessCreateTeam])   

    useEffect(() => {
        if (isSuccessUpdateTeam) {
            Notification({ 
                type: updateData?.success ? "success" : "warning",
                message: updateData?.success ? updateData.message : updateData.message, 
                description: "" 
            });
            refetch()
        }
    }, [isSuccessUpdateTeam])

    useEffect(() => {
        if(isSuccessDeleteTeam) {
            Notification({ 
                type: deleteData?.success ? "success" : "warning",
                message: deleteData?.success ? deleteData.message : deleteData.message, 
                description: "" 
            });
            refetch()
        }
    }, [isSuccessDeleteTeam])

    const addUser = () => {
        setOpen(true);
    }

    const [open, setOpen] = useState<boolean>(false);

    const cancelTap = () => {
        setOpen(false)
    }

    const saveTap = (e) => {
        createTeam(e)
        setOpen(false)
    }

    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');

    const options: SelectProps['options'] = [];

    for (let i = 10; i < 36; i++) {
        options.push({
            label: i.toString(36) + i,
            value: i.toString(36) + i,
        });
    }

    const handleChange = (value: string[]) => {
    };

    const [record, setRecord] = useState<any>(null)

    useEffect(() => {
        if (record && formRef.current) {
          const prefillStations = record.stations.map((stationId, index) => {
            return {
              label: record.stationNames[index], // Get the corresponding station name using the index
              value: stationId, // Use the stationId directly
            };
          });
      
          formRef.current.setFieldsValue({
            email: record.email,
            stations: prefillStations, // Set the prefill stations here
            firstName: record.firstName,
            lastName: record.lastName,
          });
        }
      }, [record]);

    const [editOpen, setEditOpen] = useState<boolean>(false)

    const editCancelTap = () => {
        setEditOpen(false)
    }

    const editSaveTap = (e) => {
        let modifiedStations;
        if (e.stations && Array.isArray(e.stations) && e.stations.every(station => station.hasOwnProperty('value'))) {
            modifiedStations = e.stations.map(station => station.value);
        } else {
            modifiedStations = e.stations;;
        }
        e.stations = modifiedStations;
        updateTeam(e)
        setEditOpen(false)
    }

    const formRef = useRef<any>(null);

    const [DeleteModelOpen, setDeleteModelOpen] = useState<boolean>(false)

    const deleteHandleOk = () => {
        setDeleteModelOpen(false)
        if(record) {
            deleteTeam({email: record.email})
        }
    }

    const deleteHandleCancel = () => {
        setDeleteModelOpen(false)
    }

    return (width > 768 ?
        (<Container
            backButton={false}
            loadingPageName={i18n.t("team")}
            subTitle={"team"}
            renderCondition={data ? true : false}
        >
            <div className="table">
                <div className="sessions-title">
                    <div>
                        {/* <SelectFilter
							selectedFilter={filter || undefined}
							selectedFilterValue={filterValue || undefined}
							setNewFilter={(value) => setFilter(value)}
							setNewFilterValue={(value) => setFilterValue(value)}
							items={selectItems}
							statusData={[]}
							typeData={typeData}
							stationData={stationData}
							reset={reset}
							setReset={setReset}
						/> */}
                    </div>
                    <div>
                        <Button className="dark-button" onClick={() => addUser()}>Add a Member</Button>
                        {/* <Button onClick={handleReset} className="dark-button">{i18n.t("transactions.resetFilters")}</Button> */}
                    </div>
                </div>
                {data ?
                    (<TeamTable
                        limit={limit}
                        offset={offset < 0 ? 0 : offset}
                        setOffset={setOffset}
                        length={transactions.length + (transactions.length < limit ? 0 : 1)}
                        data={getDataToDisplay(transactions, limit, offset)}
                        setNewSort={(value) => setSort(value)}
                        shouldReset={(value) => setReset(value)}
                        reset={reset}
                        setTransactions={setTransactions}
                        setReset={setReset}
                        transactions={transactions}
                        setRefundParams={setRefundParams}
                        setIsModalOpen={setIsModalOpen}
                        setRecord={setRecord}
                        setEditOpen={setEditOpen}
                        setDeleteModelOpen={setDeleteModelOpen}
                    />) :
                    null}
            </div>
            <Modal
                visible={open}
                title="Create a member"
                footer={[]}
                onOk={handleOk}
                onCancel={cancelTap}
                width={500}
            >
                <div className='model-content model-content-team'>
                    <Form
                        initialValues={{ remember: true }}
                        onFinish={saveTap}
                        autoComplete="off"
                    >
                        <div className='model-content-item3'>
                            <p>First Name: </p>
                            <Form.Item
                                label=""
                                name="firstName"
                                rules={[{ required: true, message: 'Please enter the first name.' }]}
                            >
                                <Input style={{ width: 220 }} placeholder="First Name"
                                    value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>Last Name: </p>
                            <Form.Item
                                label=""
                                name="lastName"
                                rules={[{ required: true, message: 'Please enter the last name.' }]}
                            >
                                <Input style={{ width: 220 }} placeholder="Last Name"
                                    value={lastName} onChange={(e) => setLastName(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>Email: </p>
                            <Form.Item
                                label=""
                                name="email"
                                rules={[{ required: true, message: 'Please enter the email.' }]}
                            >
                                <Input style={{ width: 220 }} placeholder="User email"
                                    value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>Stations: </p>
                            <Form.Item
                                label=""
                                name="stations"
                                rules={[{ required: true, message: 'Please select stations.' }]}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    style={{ width: 220 }}
                                    placeholder="Please select"
                                    // defaultValue={[]}
                                    onChange={handleChange}
                                    options={list}
                                />
                            </Form.Item>
                        </div>
                        <div className='model-last-btn'>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large'
                                    style={{ margin: "10px 0", height: '40px', width: '100%', background: '#FD6B7E',fontSize: '16px' }}
                                    onClick={() => cancelTap()}>{i18n.t("teams.cancel")}</Button>
                            </Form.Item>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large' htmlType="submit"
                                    style={{
                                        background: '#FF9F39', border: '1px solid #FF9F39', height: '40px',
                                        width: '100%',
                                        fontSize: '16px', fontWeight: '800'
                                    }}>{i18n.t("teams.save")}</Button>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal
                visible={editOpen}
                title="Update a member"
                footer={[]}
                onOk={handleOk}
                onCancel={editCancelTap}
                width={600}
            >
                <div className='model-content model-content-team'>
                    <Form
                        initialValues={{ email: '' }}
                        onFinish={editSaveTap}
                        autoComplete="off"
                        ref={formRef}
                    >
                        <div className='model-content-item3'>
                            <p>First Name: </p>
                            <Form.Item
                                label=""
                                name="firstName"
                                rules={[{ required: true, message: 'Please enter the first name.' }]}
                            >
                                <Input style={{ width: 220 }} placeholder="First Name"
                                    value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>Last Name: </p>
                            <Form.Item
                                label=""
                                name="lastName"
                                rules={[{ required: true, message: 'Please enter the last name.' }]}
                            >
                                <Input style={{ width: 220 }} placeholder="Last Name"
                                    value={lastName} onChange={(e) => setLastName(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>Email: </p>
                            <Form.Item
                                label=""
                                name="email"
                                rules={[{ required: true, message: 'Please enter the email.' }]}
                            >
                                <Input style={{ width: 320 }} placeholder="User email" disabled={true}
                                    value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>Stations: </p>
                            <Form.Item
                                label=""
                                name="stations"
                                rules={[{ required: true, message: 'Please select stations.' }]}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    style={{ width: 320 }}
                                    placeholder="Please select"
                                    // defaultValue={[]}
                                    onChange={handleChange}
                                    options={list}
                                />
                            </Form.Item>
                        </div>
                        <div className='model-last-btn'>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large'
                                    style={{ margin: "10px 0", height: '50px', width: '100%', background: '#FD6B7E' }}
                                    onClick={() => editCancelTap()}>{i18n.t("teams.cancel")}</Button>
                            </Form.Item>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large' htmlType="submit"
                                    style={{
                                        background: '#FF9F39', border: '1px solid #FF9F39', height: '50px',
                                        width: '100%',
                                        fontSize: '20px', fontWeight: '800'
                                    }}>{i18n.t("teams.save")}</Button>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal title="Delete a member" visible={DeleteModelOpen} onOk={deleteHandleOk} onCancel={deleteHandleCancel}
            width={400}>    
                <p style={{fontSize: '20px'}}>Are you sure you want to delete this member?</p>
            </Modal>
        </Container>) : (<Container
            backButton={false}
            loadingPageName={i18n.t("transactions.pageName")}
            subTitle={i18n.t("transactions.subtitle")}
            renderCondition={data ? true : false}
            styleNoFlex={true}
        >
            <div style={{ display: "flex", flexDirection: "row" }}>
            <Button className="dark-button" onClick={() => addUser()}>Add a Member</Button>
                {/* <Button onClick={handleReset} className="dark-button">Reset filters</Button> */}
                <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                    {/* <SelectFilter
						selectedFilter={filter || undefined}
						selectedFilterValue={filterValue || undefined}
						setNewFilter={(value) => setFilter(value)}
						setNewFilterValue={(value) => setFilterValue(value)}
						items={selectItems}
						statusData={[]}
						typeData={typeData}
						stationData={stationData}
						reset={reset}
						setReset={setReset}
					/> */}
                </div>
            </div>
            <Space style={{ marginBottom: "16px" }} direction="vertical" size={12}>
                <RangePicker onChange={setRange} format={dateFormat} value={reset || !dateRange ? undefined : [dateRange && moment(dateRange[0]), dateRange && moment(dateRange[1])]} />
            </Space>

            {data ?
                (<div className="table">
                    <TeamTable
                        limit={limit}
                        offset={offset < 0 ? 0 : offset}
                        setOffset={setOffset}
                        length={transactions.length + (transactions.length < limit ? 0 : 1)}
                        data={getDataToDisplay(transactions, limit, offset)}
                        setNewSort={(value) => setSort(value)}
                        shouldReset={(value) => setReset(value)}
                        reset={reset}
                        setTransactions={setTransactions}
                        setReset={setReset}
                        transactions={transactions}
                        setRefundParams={setRefundParams}
                        setIsModalOpen={setIsModalOpen}
                        setRecord={setRecord}
                        setEditOpen={setEditOpen}
                        setDeleteModelOpen={setDeleteModelOpen}
                    />
                </div>) :
                null}
            <Modal
                visible={open}
                title="Create a member"
                footer={[]}
                onOk={handleOk}
                onCancel={cancelTap}
                width={600}
            >
                <div className='model-content model-content-team'>
                    <Form
                        initialValues={{ remember: true }}
                        onFinish={saveTap}
                        autoComplete="off"
                    >
                        <div className='model-content-item3'>
                            <p>email: </p>
                            <Form.Item
                                label=""
                                name="email"
                                rules={[{ required: true, message: 'Please select your email!' }]}
                            >
                                <Input style={{ width: 320 }} placeholder="User Name"
                                    value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>stations: </p>
                            <Form.Item
                                label=""
                                name="stations"
                                rules={[{ required: true, message: 'Please select your stations!' }]}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    style={{ width: 320 }}
                                    placeholder="Please select"
                                    // defaultValue={[]}
                                    onChange={handleChange}
                                    options={list}
                                />
                            </Form.Item>
                        </div>
                        <div className='model-last-btn'>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large'
                                    style={{ margin: "10px 0", height: '50px', width: '100%', background: '#FD6B7E' }}
                                    onClick={() => cancelTap()}>{i18n.t("teams.cancel")}</Button>
                            </Form.Item>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large' htmlType="submit"
                                    style={{
                                        background: '#FF9F39', border: '1px solid #FF9F39', height: '50px',
                                        width: '100%',
                                        fontSize: '20px', fontWeight: '800'
                                    }}>{i18n.t("teams.save")}</Button>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal
                visible={editOpen}
                title="Update a member"
                footer={[]}
                onOk={handleOk}
                onCancel={editCancelTap}
                width={600}
            >
                <div className='model-content model-content-team'>
                    <Form
                        initialValues={{ email: '111' }}
                        onFinish={editSaveTap}
                        autoComplete="off"
                        ref={formRef}
                    >
                        <div className='model-content-item3'>
                            <p>email: </p>
                            <Form.Item
                                label=""
                                name="email"
                                rules={[{ required: true, message: 'Please select your email!' }]}
                            >
                                <Input style={{ width: 320 }} placeholder="User Name"
                                    value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Form.Item>
                        </div>
                        <div className='model-content-item3'>
                            <p>stations: </p>
                            <Form.Item
                                label=""
                                name="stations"
                                rules={[{ required: true, message: 'Please select your stations!' }]}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    style={{ width: 320 }}
                                    placeholder="Please select"
                                    // defaultValue={[]}
                                    onChange={handleChange}
                                    options={list}
                                />
                            </Form.Item>
                        </div>
                        <div className='model-last-btn'>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large'
                                    style={{ margin: "10px 0", height: '50px', width: '100%', background: '#FD6B7E' }}
                                    onClick={() => cancelTap()}>{i18n.t("teams.cancel")}</Button>
                            </Form.Item>
                            <Form.Item style={{ 'width': '48%' }}>
                                <Button type="primary" danger size='large' htmlType="submit"
                                    style={{
                                        background: '#FF9F39', border: '1px solid #FF9F39', height: '50px',
                                        width: '100%',
                                        fontSize: '20px', fontWeight: '800'
                                    }}>{i18n.t("teams.save")}</Button>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </Modal>
            <Modal title="delete a member" visible={DeleteModelOpen} onOk={deleteHandleOk} onCancel={deleteHandleCancel}>    
                <p style={{fontSize: '20px'}}>Are you sure you want to delete this member?</p>
            </Modal>
        </Container>)
    );
};

export default Teams;
